import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

// Auto-logout configuration
const IDLE_TIMEOUT_MS = 9 * 60 * 60 * 1000; // 9 hours
const WARN_BEFORE_MS = 10 * 60 * 1000; // 10 min warning

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [warehouses, setWarehouses] = useState([]);

  const refreshMe = async () => {
    const token = localStorage.getItem("wms_token");
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("wms_user", JSON.stringify(data));
    } catch (err) {
      // Server unreachable: keep the cached user so the app stays open.
      if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") {
        const cached = localStorage.getItem("wms_user");
        if (cached) {
          try {
            setUser(JSON.parse(cached));
          } catch {
            setUser(null);
          }
        } else {
          setUser(null);
        }
        return;
      }
      // Genuine auth failure (401/403 already handled by interceptor)
      setUser(null);
    }
  };

  const loadWarehouses = async () => {
    try {
      const { data } = await api.get("/warehouses");
      setWarehouses(data);
    } catch {
      /* silent */
    }
  };

  useEffect(() => {
    refreshMe();
  }, []);

  useEffect(() => {
    if (user) loadWarehouses();
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("wms_token", data.access_token);
    localStorage.setItem("wms_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("wms_token");
    localStorage.removeItem("wms_user");
    setUser(null);
    window.location.href = "/login";
  };

  // ----- Auto logout on inactivity -----
  const idleTimerRef = useRef(null);
  const warnTimerRef = useRef(null);
  const warnedRef = useRef(false);

  const resetIdle = () => {
    warnedRef.current = false;
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    warnTimerRef.current = setTimeout(() => {
      if (warnedRef.current) return;
      warnedRef.current = true;
      toast.warning("You will be signed out in 10 minutes due to inactivity.", {
        duration: 15000,
      });
    }, IDLE_TIMEOUT_MS - WARN_BEFORE_MS);
    idleTimerRef.current = setTimeout(() => {
      toast.error("Signed out due to 9 hours of inactivity.");
      setTimeout(logout, 800);
    }, IDLE_TIMEOUT_MS);
  };

  useEffect(() => {
    if (!user) return undefined;
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];
    const handler = () => resetIdle();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetIdle();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, warehouses, login, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
